import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import CustomerSearchField from "@/components/CustomerSearchField";
import FinishedProductPicker from "@/components/FinishedProductPicker";
import { toast } from "sonner";
import { Truck, Clock, PackageCheck, Plus, Check, CheckCheck, Trash2 } from "lucide-react";

const STATUS_TONE = {
  PENDING: "bg-amber-100 text-amber-900",
  DRAFT: "bg-slate-100 text-slate-900",
  IN_TRANSIT: "bg-blue-100 text-blue-900",
  DISPATCHED: "bg-violet-100 text-violet-900",
  DELIVERED: "bg-emerald-100 text-emerald-900",
  COMPLETED: "bg-emerald-100 text-emerald-900",
};

const BUCKETS = [
  { key: "pending", label: "Pending", icon: Clock, tone: "text-amber-600",
    match: (s) => ["PENDING", "DRAFT", "APPROVED", "READY", "IN_TRANSIT"].includes((s || "").toUpperCase()) },
  { key: "completed", label: "Completed", icon: PackageCheck, tone: "text-emerald-600",
    match: (s) => ["COMPLETED", "CLOSED", "INVOICED"].includes((s || "").toUpperCase()) },
  { key: "dispatched", label: "Dispatched / Delivered", icon: Truck, tone: "text-violet-600",
    match: (s) => ["DISPATCHED", "DELIVERED"].includes((s || "").toUpperCase()) },
];

const fmt = (n) => `₹${Number(n || 0).toLocaleString()}`;

const BLANK = () => ({
  customer_name: "", customer_email: "",
  vehicle_no: "", driver_name: "", eway_bill_no: "",
  sales_order_number: "", notes: "",
  lines: [{ product_id: "", product_name: "", sku: "", size: "", quantity_boxes: 1, sqm_per_box: 1, unit_price: 0 }],
  gst_percent: 18, tds_percent: 0, freight: 0,
});

export default function DistributorDispatch({ title = "Dispatch", subtitle = "Every dispatch order — pending, completed, and dispatched/delivered. Amounts flow into Collections automatically." }) {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK());

  const load = async () => {
    try { const { data } = await api.get("/org/dispatches"); setRows(data || []); } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    BUCKETS.forEach((b) => { g[b.key] = rows.filter((r) => b.match(r.status)); });
    return g;
  }, [rows]);

  const totals = useMemo(() => {
    const t = {};
    BUCKETS.forEach((b) => { t[b.key] = grouped[b.key].reduce((s, r) => s + Number(r.total_value || 0), 0); });
    return t;
  }, [grouped]);

  const visible = grouped[tab] || [];

  const addLine = () => setForm({ ...form, lines: [...form.lines, { product_id: "", product_name: "", sku: "", size: "", quantity_boxes: 1, sqm_per_box: 1, unit_price: 0 }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, ix) => ix !== i) });
  const updateLine = (i, patch) => setForm({ ...form, lines: form.lines.map((l, ix) => ix === i ? { ...l, ...patch } : l) });

  const lineSubtotal = (l) => Number(l.quantity_boxes || 0) * Number(l.sqm_per_box || 0) * Number(l.unit_price || 0);
  const subtotal = form.lines.reduce((s, l) => s + lineSubtotal(l), 0);
  const gst = subtotal * Number(form.gst_percent || 0) / 100;
  const tds = subtotal * Number(form.tds_percent || 0) / 100;
  const total = subtotal + gst + Number(form.freight || 0) - tds;

  const create = async () => {
    if (!form.customer_name) return toast.error("Pick or add a customer");
    if (!form.lines.length || !form.lines[0].product_name) return toast.error("Add at least one line item");
    try {
      const payload = {
        ...form,
        subtotal, gst_amount: gst, tds_amount: tds, total_value: total,
        total_sqm: form.lines.reduce((s, l) => s + (Number(l.quantity_boxes) * Number(l.sqm_per_box)), 0),
        status: "PENDING",
      };
      await api.post("/org/dispatches", payload);
      toast.success("Dispatch created — invoice + e-way + challan generated");
      setOpen(false); setForm(BLANK()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (d, status) => {
    try {
      await api.patch(`/org/dispatches/${d.id}`, { status });
      toast.success(`Marked ${status.toLowerCase()}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dist-dispatch">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="dist-new-dispatch-btn"><Plus className="w-4 h-4 mr-1" /> New dispatch</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl" data-testid="dist-dispatch-dialog">
            <DialogHeader><DialogTitle>New dispatch · auto-generates Invoice + E-way bill + Challan</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <CustomerSearchField
                testid="dist-dsp-customer"
                label="Customer"
                placeholder="Type customer name to search or add new…"
                value={form.customer_email || form.customer_name ? { name: form.customer_name, email: form.customer_email } : null}
                onChange={(opt) => setForm({ ...form, customer_name: opt.name || "", customer_email: opt.email || "" })}
              />
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Vehicle no.</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} data-testid="dist-dsp-vehicle" /></div>
                <div><Label>Driver</Label><Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="dist-dsp-driver" /></div>
                <div><Label>E-way bill no.</Label><Input value={form.eway_bill_no} onChange={(e) => setForm({ ...form, eway_bill_no: e.target.value })} /></div>
              </div>
              <div><Label>Sales order #</Label><Input value={form.sales_order_number} onChange={(e) => setForm({ ...form, sales_order_number: e.target.value })} data-testid="dist-dsp-so" /></div>
              <div className="space-y-2 border-t border-border pt-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Line items</Label>
                  <Button variant="outline" size="sm" onClick={addLine} data-testid="dist-dsp-add-line"><Plus className="w-3 h-3 mr-1" /> Add line</Button>
                </div>
                {form.lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,2fr)_60px_60px_80px_80px_28px] gap-2 items-end border border-border rounded-md p-2" data-testid={`dist-dsp-line-${i}`}>
                    <div>
                      <FinishedProductPicker
                        value={l.product_id ? { id: l.product_id, name: l.product_name, sku: l.sku, size: l.size, sale_price: l.unit_price } : null}
                        onChange={(p) => updateLine(i, { product_id: p.id, product_name: p.name, sku: p.sku, size: p.size, unit_price: p.sale_price })}
                        testid={`dist-dsp-line-${i}-picker`}
                      />
                    </div>
                    <div><Label className="text-[10px]">Qty box</Label><Input type="number" value={l.quantity_boxes} onChange={(e) => updateLine(i, { quantity_boxes: e.target.value })} data-testid={`dist-dsp-line-${i}-qty`} /></div>
                    <div><Label className="text-[10px]">SQM/box</Label><Input type="number" value={l.sqm_per_box} onChange={(e) => updateLine(i, { sqm_per_box: e.target.value })} /></div>
                    <div><Label className="text-[10px]">Unit ₹</Label><Input type="number" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} /></div>
                    <div className="text-right font-mono text-xs pb-2">{fmt(lineSubtotal(l))}</div>
                    <Button size="icon" variant="ghost" onClick={() => removeLine(i)} disabled={form.lines.length === 1}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 border-t border-border pt-3">
                <div><Label>GST %</Label><Input type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} /></div>
                <div><Label>TDS %</Label><Input type="number" value={form.tds_percent} onChange={(e) => setForm({ ...form, tds_percent: e.target.value })} /></div>
                <div><Label>Freight ₹</Label><Input type="number" value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-sm bg-muted/40 p-3 rounded-lg">
                <div>Subtotal <div className="font-mono font-bold">{fmt(subtotal)}</div></div>
                <div>GST <div className="font-mono font-bold">{fmt(gst)}</div></div>
                <div>TDS <div className="font-mono font-bold">- {fmt(tds)}</div></div>
                <div>Total <div className="font-mono font-black text-lg">{fmt(total)}</div></div>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} data-testid="dist-dsp-save">Create dispatch &amp; generate documents</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {BUCKETS.map((b) => {
          const Icon = b.icon; const active = tab === b.key;
          return (
            <button key={b.key} onClick={() => setTab(b.key)} data-testid={`dist-dsp-kpi-${b.key}`}
              className={`text-left rounded-2xl border transition-all ${active ? "border-orange-500 ring-2 ring-orange-500/30" : "border-border hover:border-foreground/20"} bg-card p-5`}>
              <div className="flex items-center gap-2"><Icon className={`w-4 h-4 ${b.tone}`} /><span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold">{b.label}</span></div>
              <div className="mt-2 font-display text-3xl font-black">{(grouped[b.key] || []).length}</div>
              <div className="text-xs text-muted-foreground mt-1">Value {fmt(totals[b.key])}</div>
            </button>
          );
        })}
      </div>

      <Card><CardContent className="p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Dispatch #</TableHead><TableHead>Invoice #</TableHead><TableHead>Customer</TableHead>
            <TableHead>SO #</TableHead><TableHead>Vehicle</TableHead><TableHead>SQM</TableHead>
            <TableHead>Amount</TableHead><TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.map((d) => {
              const upper = (d.status || "").toUpperCase();
              const canComplete = ["PENDING", "DRAFT", "APPROVED", "READY", "IN_TRANSIT"].includes(upper);
              const canDeliver = !["DELIVERED", "COMPLETED"].includes(upper);
              return (
                <TableRow key={d.id} data-testid={`dist-dsp-row-${d.id}`}>
                  <TableCell className="font-mono text-xs">{d.number}</TableCell>
                  <TableCell className="font-mono text-xs">{d.invoice_number || "—"}</TableCell>
                  <TableCell className="text-sm font-medium">{d.customer_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{d.sales_order_number || "—"}</TableCell>
                  <TableCell className="text-xs">{d.vehicle_no || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{Number(d.total_sqm || 0).toLocaleString()}</TableCell>
                  <TableCell className="font-mono font-semibold">{fmt(d.total_value)}</TableCell>
                  <TableCell><Badge variant="outline" className={`${STATUS_TONE[upper] || ""} font-mono text-[10px]`}>{d.status || "—"}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    {canComplete && <Button size="sm" variant="outline" onClick={() => setStatus(d, "COMPLETED")} data-testid={`dist-dsp-complete-${d.id}`}><Check className="w-3 h-3 mr-1" /> Complete</Button>}
                    {canDeliver && <Button size="sm" onClick={() => setStatus(d, "DELIVERED")} data-testid={`dist-dsp-deliver-${d.id}`}><CheckCheck className="w-3 h-3 mr-1" /> Delivered</Button>}
                  </TableCell>
                </TableRow>
              );
            })}
            {visible.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">No {tab} orders.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
